import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CropField } from '@next/arable-lib/crop-field';
import { SiteState } from '@next/core-lib/site';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  defer,
  filter,
  map,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { GetSoilCropAnalysesResponse } from 'src/app/bem/shared/models/models';
import { BemApiService } from 'src/app/bem/shared/services/bem-api.service';
import { DisplayAdvice, TgaCropFieldWithStatus, TranslationParams } from 'src/app/tga/shared/models/models';
import { TgaSharedService } from 'src/app/tga/shared/services/tga-shared.service';
import {
  cancelCultivationRequests,
  fetchCultivationAdvice,
  loadCropFields,
  loadFieldObservationImages,
  loadSatelliteImages,
} from 'src/app/tga/store/tga.actions';
import {
  selectCropFieldById,
  selectCultivationAdvice,
  selectCultivationAdviceTranslationParams,
  TgaFieldObservationImageSelectors,
  TgaSatelliteImageSelectors,
  getTgaCropYear,
} from 'src/app/tga/store/tga.selectors';
import { CropSoilAnalysisBlockComponent } from '../../../../bem/advice/components/crop-soil-analysis-block/crop-soil-analysis-block.component';
import { LoadingCardComponent } from '../../../../shared/components/loading-card.component/loading-card.component';
import { AdviceCardComponent } from '../../../components/advice-card/advice-card.component';
// eslint-disable-next-line max-len
import { SpecificFormHeaderSectionComponent } from '../../../components/questionnaire/form-header-section/specific-form-header-section/specific-form-header-section.component';
import { RiskAnalysisComponent } from '../../../components/risk-analysis/risk-analysis.component';
import { TgaSatelliteImagesComponent } from '../../../components/tga-satellite-images/tga-satellite-images.component';
import {
  BIOMASS_BENCHMARK_DATASOURCE,
  BiomassBenchmarkDatasource,
} from '../../../shared/biomass-benchmark/biomass-benchmark.datasource';
import { BiomassBenchmarkDatasourceState } from '../../../shared/biomass-benchmark/biomass-benchmark.models';
import { BiomassBenchmarkWidgetComponent } from '../../../shared/biomass-benchmark/tga-biomass-benchmark-widget.component';
import { TgaFieldObservationImagesComponent } from 'src/app/tga/components/tga-field-observation-images/tga-field-observation-images.component';
import { BlightInsightPageComponent } from 'src/app/blight-v2/components/advice/components/blight-insight-page/blight-insight-page.component';
import { BlightDashboardWidgetComponent } from 'src/app/blight-v2/components/dashboard-widget/blight-dashboard-widget.component';
import { loadCustomerBlightRights } from 'src/app/store/actions';
import { TranslatePipe } from '@next/core-lib/i18n';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
  selector: 'app-tga-cultivation-advice',
  templateUrl: './tga-cultivation-advice.component.html',
  styleUrls: ['./tga-cultivation-advice.component.scss'],
  imports: [
    SpecificFormHeaderSectionComponent,
    AdviceCardComponent,
    CropSoilAnalysisBlockComponent,
    RiskAnalysisComponent,
    LoadingCardComponent,
    TranslatePipe,
    TgaSatelliteImagesComponent,
    TgaFieldObservationImagesComponent,
    BiomassBenchmarkWidgetComponent,
    BlightInsightPageComponent,
    BlightDashboardWidgetComponent,
  ],
})
export class TgaCultivationAdviceComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly tgaSharedService = inject(TgaSharedService);
  private readonly bemApiService = inject(BemApiService);
  private readonly biomassBenchmarkDatasource = inject<BiomassBenchmarkDatasource>(BIOMASS_BENCHMARK_DATASOURCE);
  private readonly breakpointObserver = inject(BreakpointObserver);

  readonly cultivationAdvice = signal<DisplayAdvice[]>([]);
  cropFieldId?: number;
  cropYear = new Date().getFullYear();
  readonly cropField = signal<CropField | null>(null);
  readonly isLoading = signal(true);
  readonly translationParams = signal<TranslationParams>({});
  readonly biomassBenchmarkState = signal<BiomassBenchmarkDatasourceState>({ state: 'empty' });
  cropSoilAnalysisData$ = new BehaviorSubject<GetSoilCropAnalysesResponse | null>(null);
  readonly satelliteImages$ = this.store.select(TgaSatelliteImageSelectors.select);
  readonly fieldObservationImages$ = this.store.select(TgaFieldObservationImageSelectors.select);

  private readonly destroy$ = new Subject<void>();
  private readonly params$ = this.route.params;
  private readonly cultivationAdvice$ = this.store.select(selectCultivationAdvice);
  private readonly translationParams$ = this.store.select(selectCultivationAdviceTranslationParams);
  isMobile = false;

  ngOnInit(): void {
    this.store
      .select(getTgaCropYear)
      .pipe(take(1))
      .subscribe((cropYear) => {
        this.cropYear = cropYear;
        this.initializeBiomassBenchmarkData();
        this.initializeParams();
        this.translationParams$.pipe(takeUntil(this.destroy$)).subscribe((params) => {
          this.translationParams.set(params);
        });
        this.fetchBemData();
        this.checkIsMobile();
        this.store.dispatch(loadCustomerBlightRights());
      });
  }

  checkIsMobile(): void {
    this.breakpointObserver
      .observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape, Breakpoints.TabletPortrait])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isMobile = result.matches;
      });
  }

  ngOnDestroy(): void {
    this.store.dispatch(cancelCultivationRequests());
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeParams(): void {
    this.params$
      .pipe(
        tap((params: { cropFieldId?: string | number; cropYear?: string | number }) => {
          if (params.cropFieldId != null) this.cropFieldId = Number(params.cropFieldId);

          if (params.cropFieldId != null) {
            const currentCropFieldId = Number(params.cropFieldId);

            const today = new Date();
            const twentyNineDaysAgo = new Date();
            twentyNineDaysAgo.setDate(today.getDate() - 29);

            const fromDate = new Date(
              Date.UTC(
                twentyNineDaysAgo.getFullYear(),
                twentyNineDaysAgo.getMonth(),
                twentyNineDaysAgo.getDate(),
                0,
                0,
                0,
                0,
              ),
            ).toISOString();

            const toDate = new Date(
              Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999),
            ).toISOString();

            this.store.dispatch(
              loadSatelliteImages({
                cropFieldId: currentCropFieldId,
                fromDate,
                toDate,
              }),
            );
            this.store.dispatch(
              loadFieldObservationImages({
                cropFieldId: currentCropFieldId,
              }),
            );
          }
        }),
        switchMap((params) => this.store.select(selectCropFieldById(Number(params.cropFieldId)))),
        tap((cropField) => this.handleCropFieldResult(cropField)),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.handleCultivationAdviceLoading();
  }

  private handleCultivationAdviceLoading(): void {
    combineLatest([this.params$, this.cultivationAdvice$])
      .pipe(
        tap(([params, cultivationAdvice]) => {
          const currentCropFieldId = Number(params.cropFieldId);
          const currentCropYear = this.cropYear;

          const needsToFetchAdvice =
            cultivationAdvice?.cropFieldId !== currentCropFieldId || cultivationAdvice?.cropYear !== currentCropYear;

          if (needsToFetchAdvice && currentCropFieldId) {
            this.isLoading.set(true);
            this.store.dispatch(
              fetchCultivationAdvice({
                cropFieldId: currentCropFieldId,
                cropYear: currentCropYear,
              }),
            );
          } else if (cultivationAdvice) {
            this.cultivationAdvice.set(cultivationAdvice.data!);
            this.isLoading.set(false);
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private handleCropFieldResult(cropFieldWithStatus: TgaCropFieldWithStatus | null): void {
    if (!cropFieldWithStatus) {
      this.store.dispatch(loadCropFields({ cropYear: this.cropYear }));
      return;
    }

    this.cropField.set(cropFieldWithStatus.cropField);
  }

  getEndDateYear(cropField?: CropField): number | null {
    const field = cropField ?? this.cropField();
    if (!field) return this.cropYear;
    return this.tgaSharedService.getEndDateYear(field, this.cropYear);
  }

  onSatelliteImageDateRangeChange(dateRange: { startDate: Date; endDate: Date }): void {
    if (!this.cropFieldId) return;

    const startDate = new Date(
      Date.UTC(
        dateRange.startDate.getFullYear(),
        dateRange.startDate.getMonth(),
        dateRange.startDate.getDate(),
        0,
        0,
        0,
        0,
      ),
    );
    const fromDate = startDate.toISOString();

    const endDate = new Date(
      Date.UTC(
        dateRange.endDate.getFullYear(),
        dateRange.endDate.getMonth(),
        dateRange.endDate.getDate(),
        23,
        59,
        59,
        999,
      ),
    );
    const toDate = endDate.toISOString();

    this.store.dispatch(
      loadSatelliteImages({
        cropFieldId: this.cropFieldId,
        fromDate,
        toDate,
      }),
    );
  }

  private fetchBemData(): void {
    this.params$
      .pipe(
        switchMap((params) => {
          const cropFieldId = Number(params.cropFieldId);
          if (cropFieldId) {
            return this.bemApiService.getSoilCropAnalyses(cropFieldId);
          }
          return [];
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((data) => {
        this.cropSoilAnalysisData$.next(data);
      });
  }

  private initializeBiomassBenchmarkData(): void {
    this.params$
      .pipe(
        switchMap((params: { cropFieldId?: string | number; cropYear?: string | number }) => {
          const cropFieldId = Number(params.cropFieldId);

          if (!cropFieldId) {
            return of<BiomassBenchmarkDatasourceState>({ state: 'empty' });
          }

          const cropYear = this.cropYear;
          return this.store.select(selectCropFieldById(cropFieldId)).pipe(
            map((cropFieldWithStatus) => cropFieldWithStatus?.cropField?.cropVarietyId ?? null),
            filter((cropVarietyId): cropVarietyId is number => Number.isInteger(cropVarietyId) && cropVarietyId > 0),
            take(1),
            switchMap((cropVarietyId) =>
              defer(() =>
                this.biomassBenchmarkDatasource.loadData({
                  cropFieldId,
                  cropYear,
                  cropVarietyId,
                }),
              ).pipe(
                catchError(() =>
                  of<BiomassBenchmarkDatasourceState>({
                    state: 'error',
                    messageKey: 'TGA.Advice.Cultivation.BiomassBenchmark.Error',
                  }),
                ),
                startWith<BiomassBenchmarkDatasourceState>({ state: 'empty' }),
              ),
            ),
            startWith<BiomassBenchmarkDatasourceState>({ state: 'empty' }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((state) => {
        this.biomassBenchmarkState.set(state);
      });
  }

  navigateToBem(): void {
    if (this.cropFieldId) {
      this.router.navigate(['/bem', this.cropFieldId]);
    }
  }
}
